/**
 * Zalety komponentów na stronę „Zastosowane komponenty".
 *
 * Dwa różne źródła, świadomie rozdzielone:
 *
 *   falownik i magazyn → DANE BADAWCZE (tabele atrybutów w backendzie).
 *     To zdania o marce: gwarancja, serwis w Polsce, certyfikaty. Bierzemy je
 *     w całości i nie przerabiamy.
 *
 *   moduły PV → NASZE OBLICZENIA.
 *     Modułów nie ma w żadnej tabeli atrybutów. Zalety wyliczamy więc z doboru
 *     tej konkretnej instalacji — i one mówią o TYM projekcie, a nie o marce.
 */

/**
 * ═══ DLACZEGO NIE SKRACAMY ZDAŃ W POŁOWIE ═══
 *
 * Wartości z danych badawczych to pełne zdania po polsku i bywają podchwytliwe.
 * Fronius ma w gwarancji „2 lata w standardzie, ale po zarejestrowaniu wydłuża
 * się do 10". Ucięcie tego po przecinku daje „2 lata w standardzie" — zdanie
 * PRAWDZIWE gramatycznie i FAŁSZYWE w treści, na dokumencie, który firma wysyła
 * klientowi. Z tego samego powodu nie wyciągamy stąd liczb wyrażeniem
 * regularnym: pierwsza liczba w zdaniu kłamie („10,5 roku" u Sofara).
 *
 * Dlatego jedyne dozwolone skrócenie to zabranie CAŁYCH zdań od początku.
 * Nigdy fragmentu zdania.
 */
const DOMYSLNA_DLUGOSC = 150

export const skrocDoZdan = (tekst, maks = DOMYSLNA_DLUGOSC) => {
  const pelny = String(tekst || '').trim()
  if (pelny.length <= maks) return pelny

  // Granice zdań: kropka, wykrzyknik albo pytajnik, po których idzie spacja.
  // Skrótów typu „np." to nie rozróżnia, ale one nie kończą zdania i nadmiarowy
  // podział co najwyżej skróci wpis — nie zmieni jego sensu.
  const zdania = pelny.match(/[^.!?]+[.!?]+(?:\s|$)/g)
  if (!zdania) return pelny   // jedno długie zdanie bez kropki — zostaje w całości

  let wynik = ''
  for (const zdanie of zdania) {
    if (wynik && (wynik + zdanie).trim().length > maks) break
    wynik += zdanie
  }
  return (wynik || zdania[0]).trim()
}

/** Pola danych badawczych, które faktycznie są ZALETĄ, w kolejności ważności. */
const POLA_FALOWNIKA = [
  'gwarancja_lata', 'serwis_pl_czy_jest', 'aplikacja_mobilna',
  'ekosystem_baterii', 'mocne_strony', 'gwarancja_przedluzenie', 'kraj_i_producent',
]

/**
 * `gwarancja_pojemnosc_po_latach` NIE JEST na tej liście, choć to najważniejsza
 * rzecz o baterii.
 *
 * Powód: w danych badawczych to zdanie napisane dla porównywarki, nie dla
 * klienta — „70% pojemności użytkowej po 10 latach lub do osiągnięcia minimalnej
 * przetworzonej energii (throughput), zależnie co wcześniej". Nawet po odcięciu
 * zastrzeżeń zostaje żargon, którego klient nie rozumie i o który zapyta firmę.
 *
 * Zamiast tego składamy własne zdanie z LICZB policzonych przez backend
 * (`kits.jakosc_marek`) — patrz `zaletaGwarancjiMagazynu` niżej.
 */
const POLA_MAGAZYNU = [
  'certyfikaty_bezpieczenstwa', 'serwis_pl_czy_jest',
  'aplikacja_monitoring', 'kraj_i_producent',
]

/**
 * Pola, które opisują OGRANICZENIA, a nie zalety.
 *
 * „Co unieważnia gwarancję" jest ważne i zostaje w porównywarce, ale na stronie
 * przedstawiającej komponent klientowi byłoby przepisaniem ostrzeżeń pod
 * nagłówkiem „dlaczego ten sprzęt". Dokument ma nie wprowadzać w błąd w żadną
 * stronę — dlatego nie wciągamy tego do zalet i nie chowamy tego nigdzie indziej.
 */
const POLA_POMIJANE = ['gwarancja_warunki', 'serwis_pl_jak_dziala', 'monitoring_platny']

/**
 * ═══ WARTOŚCI, KTÓRE NIC NIE MÓWIĄ ═══
 *
 * Część wpisów w danych badawczych to samo potwierdzenie: pole
 * `serwis_pl_czy_jest` bywa wypełnione zdaniem „Tak.". Na liście zalet wygląda
 * to jak usterka — klient widzi ptaszek i słowo „Tak", i nie wie, czego dotyczy.
 *
 * Nie wyrzucamy takiego wpisu, bo informacja W NIM JEST: serwis w Polsce
 * naprawdę istnieje. Zamiast wartości pokazujemy wtedy ETYKIETĘ pola, czyli
 * „Oficjalny serwis w Polsce" — zdanie prawdziwe i samodzielne.
 *
 * Zaprzeczenie („Nie", „Brak") wypada z listy w całości. To nie jest zaleta
 * i nie ma czego z niego zrobić.
 */
const SAMO_POTWIERDZENIE = /^(tak|jest|dostępn|obecn)\b/i
const ZAPRZECZENIE = /^(nie|brak|niedostępn)\b/i
const PROG_UBOGIEJ_WARTOSCI = 30

/**
 * ═══ ZASTRZEŻENIA BADACZA NIE IDĄ DO OFERTY ═══
 *
 * Wartości w danych badawczych mają stałą budowę: najpierw twierdzenie, potem
 * po ŚREDNIKU zastrzeżenie osoby, która je zbierała — „nie ustalone z dostępnego
 * dokumentu", „część starszych dokumentów podaje 60%", „brak numeru VDE".
 *
 * W porównywarce to jest cenne: pokazuje, czego nie wiemy. Na dokumencie
 * wysyłanym klientowi końcowemu brzmi jak podważanie własnej oferty w połowie
 * zdania. Dlatego bierzemy wyłącznie pierwszy człon.
 *
 * Nawiasy wycinamy z tego samego powodu: niosą odnośniki i angielskie terminy
 * („(Usable Energy)", „(Minimum Throughput Energy)"), które dla klienta są
 * szumem, a nie informacją.
 */
export const oczyscZdanie = (tekst) => {
  const oczyszczone = String(tekst || '')
    .split(';')[0]
    .replace(/\s*\([^)]*\)/g, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/[\s,;:]+$/, '')
    .trim()

  // Wiodące „Tak," odpowiada na pytanie z NAZWY POLA („serwis w Polsce — czy
  // jest?"), którego klient nie widzi. Na liście zalet zostaje więc zdanie
  // zaczynające się od potwierdzenia, na które nikt nie pytał. Zdejmujemy je
  // i podnosimy pierwszą literę reszty.
  const bezPotwierdzenia = oczyszczone.replace(/^tak\s*[,:—-]\s*/i, '')
  return bezPotwierdzenia === oczyszczone
    ? oczyszczone
    : bezPotwierdzenia.charAt(0).toUpperCase() + bezPotwierdzenia.slice(1)
}

const zdanieZalety = (a) => {
  const wartosc = oczyscZdanie(a.wartosc)
  if (!wartosc) return null

  /**
   * Zdanie zaczynające się od zaprzeczenia NIE JEST zaletą — a Dyness ma
   * w polu serwisu wprost „Brak oficjalnego serwisu w Polsce". Wypisanie tego
   * pod nagłówkiem „dlaczego ten sprzęt" byłoby argumentem przeciw własnej
   * ofercie.
   *
   * Świadomie tracimy przy tym rzadkie zdania w rodzaju „Brak opłat za
   * monitoring", które są zaletą mimo przeczącego początku. To dobry kurs
   * wymiany: pokazanie klientowi wady jako zalety kosztuje znacznie więcej niż
   * pominięcie jednej prawdziwej korzyści.
   */
  if (ZAPRZECZENIE.test(wartosc)) return null

  // Samo potwierdzenie („Tak.") nic nie mówi — pokazujemy wtedy etykietę pola,
  // czyli „Oficjalny serwis w Polsce". Informacja w nim jest, brakuje kontekstu.
  if (wartosc.length <= PROG_UBOGIEJ_WARTOSCI && SAMO_POTWIERDZENIE.test(wartosc)) {
    return a.etykieta
  }
  return skrocDoZdan(wartosc)
}

const zalety = (atrybuty, kolejnosc, maks) => {
  const wg = new Map()
  ;(atrybuty || []).forEach((a) => {
    if (a?.pole && a.wartosc && !POLA_POMIJANE.includes(a.pole)) wg.set(a.pole, a)
  })

  const wybrane = []
  for (const pole of kolejnosc) {
    const a = wg.get(pole)
    if (!a) continue
    const tekst = zdanieZalety(a)
    if (!tekst) continue
    wybrane.push({
      pole,
      etykieta: a.etykieta,
      tekst,
      doPotwierdzenia: Boolean(a.do_potwierdzenia),
    })
    if (wybrane.length >= maks) break
  }
  return wybrane
}

export const zaletyFalownika = (atrybuty, maks = 4) => zalety(atrybuty, POLA_FALOWNIKA, maks)

/**
 * Gwarancja magazynu ułożona ze ZMIERZONYCH liczb, a nie z surowego zdania.
 *
 * `retencja` i `cykle` liczy backend (`kits.jakosc_marek`) — tam też opisane
 * jest, dlaczego NIE czytamy stamtąd lat gwarancji. Skoro lat nie znamy,
 * mówimy „na koniec okresu gwarancji", a nie zmyślamy okresu.
 *
 * Bez żadnej z tych liczb nie mówimy o gwarancji nic. Zdanie w rodzaju
 * „producent udziela gwarancji" jest prawdziwe o każdym sprzęcie na rynku
 * i tylko zajmuje wiersz.
 */
const zaletaGwarancjiMagazynu = (sygnaly) => {
  const retencja = Number(sygnaly?.battery_retencja) || null
  const cykle = Number(sygnaly?.battery_cykle) || null
  if (!retencja && !cykle) return null

  const czesci = []
  if (retencja) {
    czesci.push(`producent gwarantuje co najmniej ${liczba(retencja, 1)}% pojemności `
      + 'na koniec okresu gwarancji')
  }
  if (cykle) {
    czesci.push(`gwarancja obejmuje ${Math.round(cykle).toLocaleString('pl-PL')} cykli `
      + 'ładowania')
  }

  const tresc = czesci.join(', a ')
  return {
    pole: 'gwarancja_magazynu',
    etykieta: 'Gwarancja pojemności',
    tekst: tresc.charAt(0).toUpperCase() + tresc.slice(1) + '.',
  }
}

export const zaletyMagazynu = (atrybuty, sygnaly = null, maks = 4) => {
  const gwarancja = zaletaGwarancjiMagazynu(sygnaly)
  const reszta = zalety(atrybuty, POLA_MAGAZYNU, gwarancja ? maks - 1 : maks)
  // Gwarancja STOI PIERWSZA: to jedyna liczba, po której klient porównuje
  // baterie między ofertami.
  return gwarancja ? [gwarancja, ...reszta] : reszta
}

/**
 * Liczba po polsku: separator dziesiętny przecinkiem, bez zer na końcu.
 *
 * Zera obcinamy WYŁĄCZNIE gdy poprosiliśmy o miejsca dziesiętne. Bez tego
 * warunku „100 szt." zamieniłoby się w „1 szt." — wyrażenie nie widzi różnicy
 * między zerem po przecinku a zerem w liczbie całkowitej.
 */
const liczba = (n, miejsca = 0) => {
  const zapis = Number(n).toFixed(miejsca)
  return (miejsca > 0 ? zapis.replace(/\.?0+$/, '') : zapis).replace('.', ',')
}

/**
 * Czy opis ramy oznacza wykonanie w całości czarne.
 *
 * Katalog zapisuje to różnie („full black", „fullblack", „czarna rama"), więc
 * sprawdzamy oba warianty osobno zamiast szukać jednego napisu.
 */
const CZARNA_RAMA = /(full\s*black|czarn)/i
const PELNA_CZERN = /full\s*black/i

/**
 * Zalety modułów PV.
 *
 * ═══ CZEGO TU ŚWIADOMIE NIE MA ═══
 * Napięć. Zdania w rodzaju „napięcie robocze 328 V wypada w środku okna MPPT
 * (80–550 V)" są prawdziwe, ale to język karty katalogowej, a nie oferty —
 * klient końcowy nie wie, czy to dobrze, i nie ma jak sprawdzić. Warunki doboru
 * zostają tam, gdzie służą instalatorowi: na stronie schematu, na osiach
 * z zaznaczonymi granicami.
 *
 * ═══ CO ZOSTAJE ═══
 * Rzeczy, które klient rozumie bez tłumaczenia: moc i liczba sztuk, sprawność,
 * gwarancja i wygląd. Modułów nie ma w danych badawczych, więc wszystko poniżej
 * pochodzi ze specyfikacji katalogowej albo z doboru — nic nie jest wymyślone.
 *
 * Lista NIE jest dopychana do równej długości. Moduł bez znanej sprawności
 * i gwarancji pokaże jedną pozycję i tyle.
 */
export const zaletyModulu = ({ panel, uklad, spec }) => {
  const wynik = []
  const s = spec || {}

  if (panel?.pmax_w && uklad?.modulow_razem) {
    wynik.push({
      pole: 'moc_modulu',
      etykieta: 'Moc pojedynczego modułu',
      tekst: `Moc ${liczba(panel.pmax_w)} W z jednego modułu — ${uklad.modulow_razem} szt. `
        + `daje ${liczba(uklad.moc_kwp, 2)} kWp. Mocniejszy moduł to mniej sztuk `
        + 'na dachu i krótszy montaż.',
    })
  }

  if (Number(s.efficiency) > 0) {
    wynik.push({
      pole: 'sprawnosc',
      etykieta: 'Sprawność modułu',
      tekst: `Sprawność ${liczba(s.efficiency, 1)}% — tyle energii słonecznej moduł `
        + 'zamienia na prąd. Wyższa sprawność to więcej mocy z tej samej '
        + 'powierzchni dachu.',
    })
  }

  // Gwarancja PRODUKTOWA i gwarancja na SPRAWNOŚĆ to dwie różne obietnice
  // i klient myli je najczęściej ze wszystkiego. Pierwsza dotyczy sprzętu,
  // druga tego, ile mocy moduł ma jeszcze oddawać po latach.
  const produktowa = Number(s.product_warranty) > 0 ? Number(s.product_warranty) : null
  const sprawnosciowa = Number(s.eff_warranty) > 0 ? Number(s.eff_warranty) : null

  if (produktowa || sprawnosciowa) {
    const czesci = []
    if (produktowa) czesci.push(`${produktowa} lat gwarancji producenta na moduł`)
    if (sprawnosciowa) czesci.push(`${sprawnosciowa} lat gwarancji na sprawność`)
    wynik.push({
      pole: 'gwarancja_modulu',
      etykieta: 'Gwarancja',
      tekst: czesci.join(' oraz ') + '.',
    })
  }

  // Wygląd — krótko. To jedyna zaleta, którą klient ocenia sam, więc rozwlekłe
  // zdanie o niej brzmi jak przekonywanie do czegoś oczywistego.
  const rama = String(s.frame_colour || '')
  if (CZARNA_RAMA.test(rama)) {
    wynik.push({
      pole: 'estetyka',
      etykieta: 'Wygląd',
      tekst: PELNA_CZERN.test(rama)
        ? 'Wykonanie full black — jednolita czerń całego modułu, spokojna na dachu.'
        : 'Czarna rama — jednolity wygląd całej instalacji.',
    })
  }

  return wynik
}
